import {
  DIR_VEC,
  type EngineState,
  GRID,
  indexToX,
  indexToY,
  stepIndex,
  toTileViews,
} from '../engine';
import { t } from '../i18n';
import type { LayoutMetrics } from './layout';
import { PALETTE } from './palette';
import { type RingRect, type SealView, headRimDivisor, ringRects, ringThickness } from './seal';

export interface Effect {
  /** bang/aigo = 단어 종료 텍스트(필드 중앙, 1콜) · burst = 무오답 완성 8방향(8콜) — UPLIFT §6.4 */
  readonly kind: 'good' | 'bad' | 'clear' | 'relocate' | 'bang' | 'aigo' | 'burst';
  readonly cell: number;
  ttl: number;
}

const TRI8: readonly number[] = [0, 1, 2, 3, 4, 3, 2, 1];

export interface RenderInput {
  readonly prev: EngineState;
  readonly cur: EngineState;
  readonly alpha: number; // 0..1 틱 사이 보간
  readonly effects: readonly Effect[];
  readonly shake: number;
  /** 봉인 표현 뷰모델 — 압박 링(필드 테두리)과 위험 티어 머리 테두리를 그린다. */
  readonly seal?: SealView;
  /** 사망 후 경과 ms (뷰 전용, 결정론 무관). -1 이면 미사망. */
  readonly deadMs?: number;
  /** 분노 단계 0..n — 몸통·머리를 BAD 쪽으로 15%/단계(최대 60%) 섞는다(RULES §1.16 ANGER_TINT) */
  readonly anger?: number;
}

function mixHex(a: string, b: string, t: number): string {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  return rgbToHex(A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t);
}

function hexToRgb(h: string): [number, number, number] {
  const n = Number.parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number): string =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/**
 * 몸통 램프 32단 — lerp(BODY_A, BODY_B, k/31 × 0.7), 홀수 k 는 루미넌스 +8%.
 * 어두운 끝을 0.7 에서 끊는다: BODY_B 끝은 FIELD_WARN 대비 2.73:1 로 하한 미달이고,
 * 하필 2배속으로 자기 몸을 피해야 하는 상태에서 꼬리가 가장 안 보인다(UPLIFT §6.6).
 */
const BODY_RAMP: readonly string[] = (() => {
  const A = hexToRgb(PALETTE.BODY_A);
  const B = hexToRgb(PALETTE.BODY_B);
  const out: string[] = [];
  for (let k = 0; k < 32; k++) {
    const t = (k / 31) * 0.7;
    const lum = k % 2 === 1 ? 1.08 : 1;
    out.push(
      rgbToHex(
        (A[0] + (B[0] - A[0]) * t) * lum,
        (A[1] + (B[1] - A[1]) * t) * lum,
        (A[2] + (B[2] - A[2]) * t) * lum,
      ),
    );
  }
  return out;
})();

/** ADR-008 가드 — 렌더러는 타일의 존재 여부(boolean)만 본다. kind·slot 은 손에 쥐지 않는다. */
function hasTileAt(cells: readonly { cell: number }[], cell: number): boolean {
  if (cell < 0) return false;
  for (const t of cells) if (t.cell === cell) return true;
  return false;
}

export class Renderer {
  /** prefers-reduced-motion — 흔들림·플래시·꼬리 고스트를 끈다. main.ts 가 부팅 시 세팅한다. */
  reducedMotion = false;
  private ctx: CanvasRenderingContext2D;
  private staticNormal: HTMLCanvasElement | null = null;
  private staticWarn: HTMLCanvasElement | null = null;
  private sprites = new Map<string, HTMLCanvasElement>();
  private m: LayoutMetrics;
  private fontFamily: string;

  constructor(canvas: HTMLCanvasElement, m: LayoutMetrics, fontFamily: string) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (ctx === null) throw new Error('canvas 2d 컨텍스트를 얻지 못했다');
    this.ctx = ctx;
    this.m = m;
    this.fontFamily = fontFamily;
    this.resize(m);
  }

  resize(m: LayoutMetrics): void {
    this.m = m;
    this.sprites.clear();
    this.staticNormal = this.bakeStatic(false);
    this.staticWarn = this.bakeStatic(true);
    this.ctx.imageSmoothingEnabled = false;
  }

  private bakeStatic(warn: boolean): HTMLCanvasElement {
    const m = this.m;
    const c = document.createElement('canvas');
    c.width = m.canvasDeviceW;
    c.height = m.canvasDeviceH;
    const g = c.getContext('2d') as CanvasRenderingContext2D;
    g.imageSmoothingEnabled = false;

    g.fillStyle = PALETTE.VOID;
    g.fillRect(0, 0, c.width, c.height);

    const frame = m.outer;
    const fieldW = m.cell * GRID.COLS;
    const fieldH = m.cell * GRID.ROWS;

    // 벽 — strokeRect 대신 fillRect 4장
    g.fillStyle = PALETTE.WALL;
    const t = Math.max(2, Math.round(frame * 0.5));
    g.fillRect(frame - t, frame - t, fieldW + 2 * t, t);
    g.fillRect(frame - t, frame + fieldH, fieldW + 2 * t, t);
    g.fillRect(frame - t, frame, t, fieldH);
    g.fillRect(frame + fieldW, frame, t, fieldH);
    g.fillStyle = PALETTE.WALL_TOP;
    g.fillRect(frame - t, frame - t, fieldW + 2 * t, Math.max(1, Math.round(t / 3)));

    g.fillStyle = warn ? PALETTE.FIELD_WARN : PALETTE.FIELD;
    g.fillRect(frame, frame, fieldW, fieldH);

    // 그리드 점 198
    g.fillStyle = warn ? PALETTE.DOT_WARN : PALETTE.FIELD_DOT;
    const d = Math.max(1, Math.round(m.cell / 12));
    for (let y = 0; y < GRID.ROWS; y++) {
      for (let x = 0; x < GRID.COLS; x++) {
        g.fillRect(
          frame + x * m.cell + (m.cell - d) / 2,
          frame + y * m.cell + (m.cell - d) / 2,
          d,
          d,
        );
      }
    }
    return c;
  }

  /** 글자 타일 스프라이트 — 전부 동일 규격. 정답/오답 구분은 존재하지 않는다(ADR-008). */
  private sprite(ch: string): HTMLCanvasElement {
    const hit = this.sprites.get(ch);
    if (hit !== undefined) return hit;
    const m = this.m;
    const c = document.createElement('canvas');
    c.width = m.cell;
    c.height = m.cell;
    const g = c.getContext('2d') as CanvasRenderingContext2D;
    g.imageSmoothingEnabled = false;
    const pad = Math.max(1, Math.round(m.cell * 0.06));
    g.fillStyle = PALETTE.TILE_EDGE;
    g.fillRect(pad, pad, m.cell - 2 * pad, m.cell - 2 * pad);
    g.fillStyle = PALETTE.TILE;
    g.fillRect(pad + 1, pad + 1, m.cell - 2 * pad - 2, m.cell - 2 * pad - 2);
    g.fillStyle = PALETTE.TILE_INK;
    g.font = `${m.glyphDevice}px "${this.fontFamily}", monospace`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(ch, m.cell / 2, m.cell / 2 + m.glyphDevice * 0.06);
    this.sprites.set(ch, c);
    return c;
  }

  private cellX(cell: number): number {
    return this.m.outer + indexToX(cell) * this.m.cell;
  }
  private cellY(cell: number): number {
    return this.m.outer + indexToY(cell) * this.m.cell;
  }

  draw(inp: RenderInput): void {
    const { ctx, m } = this;
    const s = inp.cur;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = PALETTE.VOID;
    ctx.fillRect(0, 0, m.canvasDeviceW, m.canvasDeviceH);

    const shake = this.reducedMotion ? 0 : inp.shake;
    const sx = shake === 0 ? 0 : (shake % 2 === 0 ? 1 : -1) * Math.min(m.outer, 3);
    ctx.save();
    ctx.translate(sx, 0);

    const bg = s.penalty.status === 2 ? this.staticWarn : this.staticNormal;
    if (bg !== null) ctx.drawImage(bg, 0, 0);

    // L1 — 글자 타일
    const tiles = toTileViews(s);
    for (const t of tiles) {
      ctx.drawImage(this.sprite(t.ch), this.cellX(t.cell), this.cellY(t.cell));
    }

    // L2 — 뱀. 사망 직후 300ms 는 60ms 위상으로 점멸(fx:deathBlink) — 입력 잠금 400ms 구간을 채운다
    const blinkOff =
      !this.reducedMotion &&
      inp.deadMs !== undefined &&
      inp.deadMs >= 0 &&
      inp.deadMs < 300 &&
      Math.floor(inp.deadMs / 60) % 2 === 1;
    if (!blinkOff) this.drawSnake(inp, tiles);

    // L2.5 — 페널티 비네트: 벽 안쪽 stroke, 알파는 프레임이 아니라 **틱 인덱싱**(120Hz 에서 15Hz 점멸 방지). 1콜
    if (s.penalty.status === 2) {
      const lw = Math.max(2, Math.round(m.cell / 6));
      ctx.globalAlpha = 0.3 + 0.2 * ((TRI8[s.tick & 7] as number) / 4);
      ctx.strokeStyle = PALETTE.BAD;
      ctx.lineWidth = lw;
      ctx.strokeRect(
        m.outer + lw / 2,
        m.outer + lw / 2,
        m.cell * GRID.COLS - lw,
        m.cell * GRID.ROWS - lw,
      );
      ctx.globalAlpha = 1;
    }

    // L4 — 이펙트
    for (const fx of inp.effects) {
      if (fx.kind === 'bang' || fx.kind === 'aigo') {
        // 단어 종료 텍스트 — WORD_CLEAR(뱀 정지·타일 제거) 구간에만 살아 있다. 필드 중앙, 1콜
        const cx = m.outer + (m.cell * GRID.COLS) / 2;
        const cy = m.outer + (m.cell * GRID.ROWS) / 2;
        ctx.globalAlpha = Math.max(0.2, fx.ttl / 6);
        ctx.fillStyle = fx.kind === 'bang' ? PALETTE.ACCENT : PALETTE.NEUTRAL;
        ctx.font = `${Math.round(m.cell * 0.9)}px ${this.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(fx.kind === 'bang' ? t().effects.bang : t().effects.miss, cx, cy);
        ctx.globalAlpha = 1;
        continue;
      }
      if (fx.kind === 'burst') {
        // 무오답 완성 — 머리에서 8방향 균등 샘플, 8콜
        const cx = this.cellX(fx.cell) + m.cell / 2;
        const cy = this.cellY(fx.cell) + m.cell / 2;
        const r = (1 - fx.ttl / 6) * m.cell * 1.4;
        const sz = Math.max(2, Math.round(m.cell * 0.18));
        ctx.fillStyle = PALETTE.ACCENT;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            ctx.fillRect(cx + dx * r - sz / 2, cy + dy * r - sz / 2, sz, sz);
          }
        }
        continue;
      }
      const x = this.cellX(fx.cell);
      const y = this.cellY(fx.cell);
      const grow = (1 - fx.ttl / 12) * m.cell * 0.5;
      ctx.strokeStyle =
        fx.kind === 'good' ? PALETTE.GOOD : fx.kind === 'bad' ? PALETTE.BAD : PALETTE.NEUTRAL;
      ctx.lineWidth = Math.max(2, Math.round(m.cell / 10));
      ctx.strokeRect(x - grow, y - grow, m.cell + grow * 2, m.cell + grow * 2);
    }

    // L3.5 — 봉인 압박 링. 필드 테두리(벽 띠)를 아래 중앙에서 위 중앙으로 채운다. 최대 6 콜
    if (inp.seal !== undefined) this.drawSealRing(inp.seal);

    ctx.restore();
  }

  private ringBuf: RingRect[] = [];

  /**
   * 압박은 링이, 진행은 띠의 도장이 소유한다(seal.ts). 티어 0/1/2 는 두께(기하)로 갈리고
   * 색(CALM/WARN/CRIT)은 2차 신호다. 티어 2 는 틱 인덱싱 색 교대 맥동(최속 틱 60ms 에서 2.1Hz — WCAG 2.3.1 여유).
   */
  private drawSealRing(v: SealView): void {
    const { ctx, m } = this;
    const wall = Math.max(2, Math.round(m.outer * 0.5));
    const n = ringRects(
      {
        originX: m.outer,
        originY: m.outer,
        fieldW: m.cell * GRID.COLS,
        fieldH: m.cell * GRID.ROWS,
        thickness: ringThickness(v.tier, wall),
      },
      v.ringFill,
      this.ringBuf,
    );
    if (n === 0) return;
    // 티어 2 맥동은 alpha 가 아니라 **색 교대**(CRIT ↔ WARN)다 — alpha 0.55 는 WALL 위 합성 대비가
    // 1.48:1 로 무너져 테스트가 지키는 색쌍 대비(2.26)가 화면에 안 그려진다. reducedMotion 이면 CRIT 고정.
    ctx.fillStyle =
      v.tier === 2
        ? v.critOn || this.reducedMotion
          ? PALETTE.SEAL_CRIT
          : PALETTE.SEAL_WARN
        : v.tier === 1
          ? PALETTE.SEAL_WARN
          : PALETTE.SEAL_CALM;
    for (let i = 0; i < n; i++) {
      const r = this.ringBuf[i] as RingRect;
      ctx.fillRect(r.x, r.y, r.w, r.h);
    }
  }

  /**
   * 뱀 — 도트 아트 0, 전부 fillRect (UPLIFT §6.6).
   * 관절 다리 · 꼬리 테이퍼 · 32단 램프 · 방향 눈 · 앞에 타일이 있으면 입 · 페널티 표정 · 꼬리 고스트.
   */
  private tintCache = new Map<number, { ramp: string[]; head: string }>();
  private angerColors(stage: number): { ramp: string[]; head: string } {
    const k = Math.max(0, Math.min(4, stage));
    const hit = this.tintCache.get(k);
    if (hit !== undefined) return hit;
    const t = Math.min(0.6, 0.15 * k);
    const v = {
      ramp: BODY_RAMP.map((c) => (t === 0 ? c : mixHex(c, PALETTE.BAD, t))),
      head: t === 0 ? PALETTE.HEAD : mixHex(PALETTE.HEAD, PALETTE.BAD, t),
    };
    this.tintCache.set(k, v);
    return v;
  }

  private drawSnake(inp: RenderInput, tiles: readonly { cell: number; ch: string }[]): void {
    const { ctx, m } = this;
    const s = inp.cur;
    const tint = this.angerColors(inp.anger ?? 0);
    const cells = s.snake.cells;
    const prevCells = inp.prev.snake.cells;
    const len = cells.length;
    const a = inp.alpha;
    const cell = m.cell;

    const bodyInset = Math.max(1, Math.round(cell * 0.07));
    const headInset = Math.max(1, Math.round(cell * 0.04));

    // 보간된 셀 좌상단 좌표
    const pos = (i: number): [number, number] => {
      const cur = cells[i] as number;
      const old = (prevCells[i] ?? cur) as number;
      return [
        this.cellX(old) + (this.cellX(cur) - this.cellX(old)) * a,
        this.cellY(old) + (this.cellY(cur) - this.cellY(old)) * a,
      ];
    };
    // 세그먼트 i 의 변 길이 (꼬리 3칸은 cell*0.53 까지 테이퍼)
    const side = (i: number): number => {
      const base = cell - 2 * (i === 0 ? headInset : bodyInset);
      const fromTail = len - 1 - i;
      if (fromTail >= 3) return base;
      const t = 1 - fromTail / 3; // 꼬리 끝 1 → 3칸 앞 0
      return base + (cell * 0.53 - base) * t;
    };

    // 꼬리 고스트 — 성장하지 않은 틱에 이전 꼬리 칸이 수축한다 (SPEC 12.6). reduced-motion 이면 생략
    if (!this.reducedMotion && prevCells.length >= len && prevCells.length > 0) {
      const oldTail = prevCells[prevCells.length - 1] as number;
      if (!cells.includes(oldTail)) {
        const g = cell * 0.53 * (1 - a);
        if (g > 1) {
          ctx.globalAlpha = 0.6 * (1 - a);
          ctx.fillStyle = BODY_RAMP[31] as string;
          ctx.fillRect(
            this.cellX(oldTail) + (cell - g) / 2,
            this.cellY(oldTail) + (cell - g) / 2,
            g,
            g,
          );
          ctx.globalAlpha = 1;
        }
      }
    }

    // 몸통 — 꼬리부터 그려 머리가 위에 오게 한다.
    // 세그먼트 i 는 자기 정사각형과 i-1 쪽 다리를 **한 장의 fillRect** 로 그린다 —
    // 이웃 중심까지 지배 축으로 늘인 사각형이라 지그재그를 포함한 어떤 형상에서도 몸통 콜 = L−1 이다.
    // 다리를 따로 그리면 2L 이라 길이 40 에서 예산 110 을 넘었다(tools/drawbudget.ts 실측 122콜).
    // 8% inset 만 두고 다리를 안 그리면 "블록 5개"로 읽히므로 다리 자체는 유지한다.
    for (let i = len - 1; i >= 1; i--) {
      const [x, y] = pos(i);
      const sd = side(i);
      ctx.fillStyle = tint.ramp[Math.min(31, i)] as string;
      const [nx, ny] = pos(i - 1);
      const cx = x + cell / 2;
      const cy = y + cell / 2;
      const ncx = nx + cell / 2;
      const ncy = ny + cell / 2;
      if (Math.abs(ncx - cx) >= Math.abs(ncy - cy)) {
        const x0 = Math.min(cx - sd / 2, ncx);
        const x1 = Math.max(cx + sd / 2, ncx);
        ctx.fillRect(x0, cy - sd / 2, x1 - x0, sd);
      } else {
        const y0 = Math.min(cy - sd / 2, ncy);
        const y1 = Math.max(cy + sd / 2, ncy);
        ctx.fillRect(cx - sd / 2, y0, sd, y1 - y0);
      }
    }

    // 머리
    if (len === 0) return;
    const [hx, hy] = pos(0);
    const hs = side(0);
    const hx0 = hx + (cell - hs) / 2;
    const hy0 = hy + (cell - hs) / 2;
    ctx.fillStyle = tint.head;
    ctx.fillRect(hx0, hy0, hs, hs);
    // rim — 항상 INK(페널티 중 BAD 로 바꾸면 BODY_A 대비 1.26:1 로 윤곽이 사라진다).
    // 위험 티어는 색이 아니라 두께로 갈린다 — 중심시야에 있는 유일한 티어 채널이다(seal.ts).
    ctx.strokeStyle = PALETTE.INK;
    ctx.lineWidth = Math.max(2, Math.round(cell / headRimDivisor(inp.seal?.tier ?? 0)));
    ctx.strokeRect(hx0, hy0, hs, hs);

    // 눈 — 진행 방향 기준. f = 전방, r = 오른쪽 수직
    const f = DIR_VEC[s.snake.dir] as readonly [number, number];
    const rx = -f[1];
    const ry = f[0];
    const ccx = hx + cell / 2;
    const ccy = hy + cell / 2;
    const e = Math.max(2, Math.round(cell / 7));
    const eh = s.penalty.status === 2 ? Math.max(1, Math.round(e / 2)) : e; // 페널티 표정: 눈 절반
    const fwd = cell * 0.18;
    const spread = cell * 0.2;
    ctx.fillStyle = PALETTE.INK;
    for (const sgn of [-1, 1]) {
      const ex = ccx + f[0] * fwd + rx * spread * sgn;
      const ey = ccy + f[1] * fwd + ry * spread * sgn;
      ctx.fillRect(Math.round(ex - e / 2), Math.round(ey - eh / 2), e, eh);
    }

    // 입 — 진행 방향 앞 칸에 타일이 있으면 앞면에 노치. 존재 여부(boolean)만 본다 (ADR-008)
    const ahead = stepIndex(cells[0] as number, s.snake.dir);
    if (hasTileAt(tiles, ahead)) {
      const n = Math.max(2, Math.round(cell * 0.12));
      const mx = ccx + f[0] * (hs / 2 - n / 2);
      const my = ccy + f[1] * (hs / 2 - n / 2);
      ctx.fillStyle = PALETTE.FIELD;
      ctx.fillRect(Math.round(mx - n / 2), Math.round(my - n / 2), n, n);
    }

    // 스피드 트레일 — 페널티 중 머리 뒤에 얇은 선 2개
    if (s.penalty.status === 2) {
      const tl = cell * 0.5;
      const tw = Math.max(1, Math.round(cell / 14));
      ctx.globalAlpha = 0.45;
      ctx.fillStyle = PALETTE.HEAD;
      for (const sgn of [-1, 1]) {
        const bx = ccx - f[0] * (hs / 2 + tl) + rx * spread * sgn;
        const by = ccy - f[1] * (hs / 2 + tl) + ry * spread * sgn;
        if (f[0] !== 0) ctx.fillRect(Math.min(bx, bx + f[0] * tl), by - tw / 2, tl, tw);
        else ctx.fillRect(bx - tw / 2, Math.min(by, by + f[1] * tl), tw, tl);
      }
      ctx.globalAlpha = 1;
    }
  }
}
